import { ContractStatusEnum, IContract } from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { format } from 'date-fns/format';
import {
  CONTRACT_LINEAGE_END_ACTION_OUTCOME,
  CONTRACT_STATUS_DISPLAY,
} from '../constants/contracts';

export type LineageContract = Pick<
  IContract,
  | 'id'
  | 'previousContractId'
  | 'status'
  | 'startDate'
  | 'endDate'
  | 'endAction'
  | 'billGroupId'
>;

/**
 * An ordered chain of contracts (original term → renewals) linked by
 * `previousContractId`. Chains can span bill groups.
 */
export interface ContractLineage<T extends LineageContract = IContract> {
  /** Id of the oldest contract in the chain */
  id: string;
  /** Oldest → newest; term number is index + 1 */
  contracts: T[];
  /** The term that represents the lineage today */
  currentContractId: string;
  /** A PENDING early renewal that follows an ACTIVE current term */
  upcomingContractId: string | null;
  /** Unique bill groups across the chain, oldest → newest */
  billGroupIds: string[];
}

const toIsoDate = (date: Date) => format(date, 'yyyy-MM-dd');

const compareTerms = (a: LineageContract, b: LineageContract) => {
  if (a.startDate !== b.startDate) {
    return a.startDate < b.startDate ? -1 : 1;
  }
  return a.id < b.id ? -1 : 1;
};

/**
 * Decides which term represents the lineage today, and whether an early
 * renewal is waiting behind it. Expects terms ordered oldest → newest.
 */
export function pickCurrentTerm<T extends LineageContract>(
  terms: T[],
  today: Date,
): Pick<ContractLineage<T>, 'currentContractId' | 'upcomingContractId'> {
  const todayIso = toIsoDate(today);
  const active = terms.filter(
    ({ status }) => status === ContractStatusEnum.ACTIVE,
  );
  const pending = terms.filter(
    ({ status }) => status === ContractStatusEnum.PENDING,
  );

  let current: T;
  if (active.length === 1) {
    [current] = active;
  } else if (active.length > 1) {
    current =
      active.find(
        ({ startDate, endDate }) =>
          startDate <= todayIso && todayIso <= endDate,
      ) ?? active[active.length - 1];
  } else if (pending.length > 0) {
    [current] = pending;
  } else {
    current = terms.reduce((latest, term) =>
      term.endDate >= latest.endDate ? term : latest,
    );
  }

  const upcoming =
    current.status === ContractStatusEnum.ACTIVE
      ? pending.find(({ startDate }) => startDate >= current.startDate)
      : undefined;

  return {
    currentContractId: current.id,
    upcomingContractId: upcoming?.id ?? null,
  };
}

/**
 * Groups contracts into lineages using `previousContractId` links.
 * Links decide grouping, start dates decide order. A link to a contract that
 * is not in the list is ignored, so that contract starts its own lineage.
 */
export function buildContractLineages<T extends LineageContract>(
  contracts: T[],
  today: Date,
): ContractLineage<T>[] {
  const contractsById = new Map(contracts.map((item) => [item.id, item]));
  const parentById = new Map(contracts.map(({ id }) => [id, id]));

  // Union-find keeps this safe against cycles and branches in bad data
  const findRoot = (id: string): string => {
    let rootId = id;
    let parentId = parentById.get(rootId);
    while (parentId && parentId !== rootId) {
      rootId = parentId;
      parentId = parentById.get(rootId);
    }
    parentById.set(id, rootId);
    return rootId;
  };

  contracts.forEach(({ id, previousContractId }) => {
    if (previousContractId && contractsById.has(previousContractId)) {
      const rootA = findRoot(id);
      const rootB = findRoot(previousContractId);
      if (rootA !== rootB) {
        parentById.set(rootA, rootB);
      }
    }
  });

  const groups = new Map<string, T[]>();
  contracts.forEach((item) => {
    const rootId = findRoot(item.id);
    const group = groups.get(rootId);
    if (group) {
      group.push(item);
    } else {
      groups.set(rootId, [item]);
    }
  });

  return Array.from(groups.values()).map((group) => {
    const terms = [...group].sort(compareTerms);
    return {
      id: terms[0].id,
      contracts: terms,
      ...pickCurrentTerm(terms, today),
      billGroupIds: Array.from(
        new Set(terms.map(({ billGroupId }) => billGroupId).filter(Boolean)),
      ),
    };
  });
}

export function getLineageContract<T extends LineageContract>(
  lineage: ContractLineage<T>,
  contractId: string,
): T | undefined {
  return lineage.contracts.find(({ id }) => id === contractId);
}

export function getCurrentLineageContract<T extends LineageContract>(
  lineage: ContractLineage<T>,
): T {
  return (
    getLineageContract(lineage, lineage.currentContractId) ??
    lineage.contracts[lineage.contracts.length - 1]
  );
}

export function findLineageForContract<T extends LineageContract>(
  lineages: ContractLineage<T>[],
  contractId: string,
): ContractLineage<T> | undefined {
  return lineages.find((lineage) =>
    lineage.contracts.some(({ id }) => id === contractId),
  );
}

/**
 * Files renewal quotes under the lineage of the contract they renew
 * (`previousContractId`). Quotes renewing a contract outside `lineages` are dropped.
 */
export function groupRenewalQuotesByLineage<
  T extends LineageContract,
  Q extends { previousContractId?: string | null },
>(lineages: ContractLineage<T>[], quotes: Q[]): Map<string, Q[]> {
  const lineageIdByContractId = new Map(
    lineages.flatMap(({ id: lineageId, contracts }) =>
      contracts.map(({ id }) => [id, lineageId] as const),
    ),
  );

  const quotesByLineageId = new Map<string, Q[]>();
  quotes.forEach((quote) => {
    const lineageId = quote.previousContractId
      ? lineageIdByContractId.get(quote.previousContractId)
      : undefined;
    if (!lineageId) {
      return;
    }
    const lineageQuotes = quotesByLineageId.get(lineageId);
    if (lineageQuotes) {
      lineageQuotes.push(quote);
    } else {
      quotesByLineageId.set(lineageId, [quote]);
    }
  });
  return quotesByLineageId;
}

const STATUS_SORT_RANK: Record<ContractStatusEnum, number> = {
  [ContractStatusEnum.ACTIVE]: 0,
  [ContractStatusEnum.PENDING]: 1,
  [ContractStatusEnum.FINISHED]: 2,
  [ContractStatusEnum.CANCELED]: 2,
};

/**
 * "Attention first": active lineages by soonest end, then pending-only
 * lineages by start, then ended lineages with the most recent first.
 */
export function sortContractLineages<T extends LineageContract>(
  lineages: ContractLineage<T>[],
): ContractLineage<T>[] {
  return [...lineages].sort((lineageA, lineageB) => {
    const currentA = getCurrentLineageContract(lineageA);
    const currentB = getCurrentLineageContract(lineageB);
    const rankA = STATUS_SORT_RANK[currentA.status];
    const rankB = STATUS_SORT_RANK[currentB.status];
    if (rankA !== rankB) {
      return rankA - rankB;
    }
    if (currentA.status === ContractStatusEnum.PENDING) {
      return currentA.startDate.localeCompare(currentB.startDate);
    }
    if (rankA === STATUS_SORT_RANK[ContractStatusEnum.FINISHED]) {
      return currentB.endDate.localeCompare(currentA.endDate);
    }
    return currentA.endDate.localeCompare(currentB.endDate);
  });
}

/** Short description of what happens next, e.g. "Ends Jun 1, 2027, then auto-renews" */
export function getLineageNextEvent<T extends LineageContract>(
  lineage: ContractLineage<T>,
  today: Date,
): string {
  const current = getCurrentLineageContract(lineage);
  const upcoming = lineage.upcomingContractId
    ? getLineageContract(lineage, lineage.upcomingContractId)
    : undefined;
  const hasStarted = (startDate: string) => startDate <= toIsoDate(today);

  switch (current.status) {
    case ContractStatusEnum.ACTIVE:
      if (upcoming) {
        return hasStarted(upcoming.startDate)
          ? `Renewal started ${toDateShort(upcoming.startDate)}`
          : `Renewal starts ${toDateShort(upcoming.startDate)}`;
      }
      return `Ends ${toDateShort(current.endDate)}, ${CONTRACT_LINEAGE_END_ACTION_OUTCOME[current.endAction]}`;
    case ContractStatusEnum.PENDING:
      return hasStarted(current.startDate)
        ? `Started ${toDateShort(current.startDate)}`
        : `Starts ${toDateShort(current.startDate)}`;
    case ContractStatusEnum.CANCELED:
      return `Canceled ${toDateShort(current.endDate)}`;
    default:
      return `Finished ${toDateShort(current.endDate)}`;
  }
}

/**
 * Accessible name for a term, e.g. "Term 4 of 4, current, Jun 1, 2026 to May 31, 2027, Active".
 * Includes the Current / Upcoming markers, since an explicit label hides the visible ones.
 */
export function getTermAriaLabel(
  { startDate, endDate, status }: LineageContract,
  termNumber: number,
  termCount: number,
  {
    isCurrent = false,
    isUpcoming = false,
  }: { isCurrent?: boolean; isUpcoming?: boolean } = {},
): string {
  const marker = isCurrent ? ', current' : isUpcoming ? ', upcoming' : '';
  return `Term ${termNumber} of ${termCount}${marker}, ${toDateShort(startDate)} to ${toDateShort(endDate)}, ${CONTRACT_STATUS_DISPLAY[status]}`;
}

/**
 * Swaps the contract id segment in a route that renders the contract modal.
 * Contract ids are prefixed (`ctrct_`) so they only ever match their own segment.
 */
export function replaceContractIdInPathname(
  pathname: string,
  currentContractId: string,
  nextContractId: string,
): string {
  return pathname
    .split('/')
    .map((segment) =>
      segment === currentContractId ? nextContractId : segment,
    )
    .join('/');
}
